
import React, { useState } from 'react'
import { Plus, Search, Filter, Download, FileText, Edit, Trash2, Eye, CheckCircle, XCircle, Clock, Package, ShoppingCart, DollarSign, TrendingUp, AlertTriangle, Send } from 'lucide-react'
import { useCompras } from '../hooks/useCompras'
import { useRequisicoes } from '../hooks/useRequisicoes'
import { useInsumos } from '../hooks/useInsumos'
import { usePlanejamentoObras } from '../hooks/usePlanejamentoObras'
import MetricsCard from '../components/MetricsCard'
import RequisicaoForm from '../components/RequisicaoForm'
import CotacaoLoteForm from '../components/CotacaoLoteForm'
import FornecedorForm from '../components/FornecedorForm'
import NotificationBellCompras from '../components/NotificationBellCompras'
import { exportComprasToPDF, exportComprasToExcel, exportFornecedoresToPDF, exportOrdensCompraToPDF, exportOrdensCompraToExcel } from '../utils/comprasExportUtils'

const Compras: React.FC = () => {
  const { fornecedores, createFornecedor, updateFornecedor, toggleBlockFornecedor, deleteFornecedor } = useCompras()
  const {
    requisicoes,
    ordensCompra,
    loading,
    metrics,
    createRequisicao,
    updateRequisicao,
    approveRequisicao,
    rejectRequisicao,
    addCotacoes,
    generateOrdemCompra,
    enviarOrdemParaFinanceiro,
    deleteRequisicao
  } = useRequisicoes()

  const { insumos } = useInsumos()
  const { obras } = usePlanejamentoObras()

  const [showRequisicaoForm, setShowRequisicaoForm] = useState(false)
  const [showFornecedorForm, setShowFornecedorForm] = useState(false)
  const [showCotacaoLoteForm, setShowCotacaoLoteForm] = useState(false)
  const [editingRequisicao, setEditingRequisicao] = useState<any>(null)
  const [editingFornecedor, setEditingFornecedor] = useState<any>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [activeTab, setActiveTab] = useState<'requisicoes' | 'aprovacao' | 'cotacao' | 'ordens' | 'fornecedores'>('requisicoes')
  const [selectedRequisicoes, setSelectedRequisicoes] = useState<string[]>([])
  const [viewingCotacoes, setViewingCotacoes] = useState<any>(null)

  // Filter requisicoes
  const filteredRequisicoes = requisicoes.filter(req => {
    const matchesSearch = req.nomeInsumo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         req.numero.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || req.status === statusFilter
    return matchesSearch && matchesStatus
  })

  // Get requisicoes by status
  const requisicoesParaAprovacao = requisicoes.filter(r => r.status === 'pendente')
  const requisicoesParaCotacao = requisicoes.filter(r => r.status === 'aprovada')
  const requisicoesCotadas = requisicoes.filter(r => r.status === 'cotada')

  // Filter fornecedores  
  const filteredFornecedores = fornecedores.filter(fornecedor => {
    return fornecedor.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
           fornecedor.segmento.toLowerCase().includes(searchTerm.toLowerCase())
  })

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pendente': return 'bg-yellow-100 text-yellow-800'
      case 'aprovada': return 'bg-green-100 text-green-800'
      case 'rejeitada': return 'bg-red-100 text-red-800'
      case 'em_cotacao': return 'bg-blue-100 text-blue-800'
      case 'cotada': return 'bg-purple-100 text-purple-800'
      case 'ordem_compra': return 'bg-indigo-100 text-indigo-800'
      case 'emitida': return 'bg-blue-100 text-blue-800'
      case 'enviada_financeiro': return 'bg-green-100 text-green-800'
      case 'finalizada': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // Get status label
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pendente': return 'Pendente Aprovação'
      case 'aprovada': return 'Aprovada'
      case 'rejeitada': return 'Rejeitada'
      case 'em_cotacao': return 'Em Cotação'
      case 'cotada': return 'Cotada'
      case 'ordem_compra': return 'Ordem de Compra'
      case 'emitida': return 'Emitida'
      case 'enviada_financeiro': return 'Enviada ao Financeiro'
      case 'finalizada': return 'Finalizada'
      default: return status
    }
  }

  // Get obra info
  const getObraInfo = (requisicao: any) => {
    if (!requisicao.obraId) return { nomeObra: 'N/A', etapa: 'N/A' }
    
    const obra = obras.find(o => o._id === requisicao.obraId)
    if (!obra) return { nomeObra: 'N/A', etapa: 'N/A' }

    const etapa = obra.etapas?.find((e: any) => e.id === requisicao.etapaId)
    
    return {
      nomeObra: obra.nomeObra || 'N/A',
      etapa: etapa ? etapa.nome : 'N/A'
    }
  }

  // Handle approve
  const handleApprove = async (id: string) => {
    if (confirm('Confirma a aprovação desta requisição?')) {
      await approveRequisicao(id)
    }
  }

  // Handle reject
  const handleReject = async (id: string) => {
    const motivo = prompt('Motivo da rejeição:')
    if (motivo) {
      await rejectRequisicao(id, motivo)
    }
  }

  // Handle select requisicao for batch quotation
  const handleSelectRequisicao = (id: string, selected: boolean) => {
    if (selected) {
      setSelectedRequisicoes(prev => [...prev, id])
    } else {
      setSelectedRequisicoes(prev => prev.filter(reqId => reqId !== id))
    }
  }

  // Handle batch quotation
  const handleBatchQuotation = () => {
    if (selectedRequisicoes.length === 0) {
      alert('Selecione pelo menos uma requisição')
      return
    }
    
    const requisicoesSelecionadas = requisicoesParaCotacao.filter(r => selectedRequisicoes.includes(r._id))
    setShowCotacaoLoteForm(true)
  }

  // Handle generate order
  const handleGenerateOrder = async (requisicaoId: string) => {
    if (confirm('Confirma a geração da ordem de compra?')) {
      await generateOrdemCompra(requisicaoId)
    }
  }

  // Handle send to finance
  const handleSendToFinance = async (ordemId: string) => {
    if (confirm('Confirma o envio desta ordem para o financeiro como projeção de pagamento?')) {
      await enviarOrdemParaFinanceiro(ordemId)
    }
  }

  // Handle select quotation
  const handleSelectQuotation = async (requisicaoId: string, cotacaoIndex: number) => {
    const requisicao = requisicoes.find(r => r._id === requisicaoId)
    if (!requisicao) return

    const cotacoes = requisicao.cotacoes.map((c: any, index: number) => ({
      ...c,
      selecionado: index === cotacaoIndex
    }))

    await updateRequisicao(requisicaoId, { cotacoes })
  }

  // Handle delete
  const handleDelete = async (id: string, type: 'requisicao' | 'fornecedor') => {
    if (confirm(`Tem certeza que deseja excluir esta ${type}?`)) {
      if (type === 'requisicao') {
        await deleteRequisicao(id)
      } else {
        await deleteFornecedor(id)
      }
    }
  }

  // Handle block fornecedor
  const handleBlockFornecedor = async (fornecedor: any) => {
    const motivo = fornecedor.bloqueado ? 
      '' : 
      prompt('Motivo do bloqueio:')
    
    if (fornecedor.bloqueado || motivo) {
      await toggleBlockFornecedor(fornecedor._id, motivo)
    }
  }

  // Handle export orders
  const handleExportOrdensPDF = () => {
    if (ordensCompra.length === 0) {
      alert('Não há ordens de compra para exportar')
      return
    }
    exportOrdensCompraToPDF(ordensCompra, fornecedores)
  }

  const handleExportOrdensExcel = () => {
    if (ordensCompra.length === 0) {
      alert('Não há ordens de compra para exportar')
      return
    }
    exportOrdensCompraToExcel(ordensCompra, fornecedores)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando dados de compras...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Compras</h1>
              <p className="text-gray-600 mt-1">Gestão de requisições, cotações e ordens de compra</p>
            </div>
            <div className="flex items-center gap-4">
              <NotificationBellCompras />
              <button
                onClick={() => setShowRequisicaoForm(true)}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors"
              >
                <Plus size={20} />
                Nova Requisição
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Métricas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6 mb-8">
          <MetricsCard
            title="Total Requisições"
            value={metrics.totalRequisicoes}
            icon={Package}
            color="#10B981"
            description="Todas as requisições"
          />
          <MetricsCard
            title="Pendentes Aprovação"
            value={metrics.pendentesAprovacao}
            icon={Clock}
            color="#F59E0B"
            description="Aguardando aprovação"
          />
          <MetricsCard
            title="Aprovadas"
            value={metrics.aprovadas}
            icon={CheckCircle}
            color="#059669"
            description="Prontas para cotação"
          />
          <MetricsCard
            title="Em Cotação"
            value={metrics.emCotacao}
            icon={ShoppingCart}
            color="#3B82F6"
            description="Sendo cotadas"
          />
          <MetricsCard
            title="Ordens Emitidas"
            value={metrics.ordensEmitidas}
            icon={FileText}
            color="#8B5CF6"
            description="Ordens de compra"
          />
          <MetricsCard
            title="Valor Total"
            value={formatCurrency(metrics.valorTotalOrdens)}
            icon={DollarSign}
            color="#EF4444"
            description="Valor das ordens"
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              <button
                onClick={() => setActiveTab('requisicoes')}
                className={`py-4 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'requisicoes'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Requisições ({filteredRequisicoes.length})
              </button>
              <button
                onClick={() => setActiveTab('aprovacao')}
                className={`py-4 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'aprovacao'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Aprovação ({requisicoesParaAprovacao.length})
              </button>
              <button
                onClick={() => setActiveTab('cotacao')}
                className={`py-4 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'cotacao'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Cotação ({requisicoesParaCotacao.length})
              </button>
              <button
                onClick={() => setActiveTab('ordens')}
                className={`py-4 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'ordens'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Ordens ({ordensCompra.length})
              </button>
              <button
                onClick={() => setActiveTab('fornecedores')}
                className={`py-4 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'fornecedores'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Fornecedores ({fornecedores.length})
              </button>
            </nav>
          </div>

          {/* Requisições Tab */}
          {activeTab === 'requisicoes' && (
            <>
              {/* Filtros e Busca */}
              <div className="p-6 border-b">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      placeholder="Buscar por insumo ou número..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Filter size={20} className="text-gray-400" />
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">Todos os Status</option>
                      <option value="pendente">Pendente</option>
                      <option value="aprovada">Aprovada</option>
                      <option value="rejeitada">Rejeitada</option>
                      <option value="em_cotacao">Em Cotação</option>
                      <option value="cotada">Cotada</option>
                      <option value="ordem_compra">Ordem de Compra</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Lista de Requisições */}
              <div className="overflow-x-auto">
                {filteredRequisicoes.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma requisição encontrada</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {searchTerm || statusFilter !== 'all' 
                        ? 'Tente ajustar os filtros de busca'
                        : 'Comece criando sua primeira requisição'
                      }
                    </p>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Número
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Insumo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantidade
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Responsável Req.
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Obra/Etapa
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Data
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredRequisicoes.map((requisicao) => {
                        const obraInfo = getObraInfo(requisicao)

                        return (
                          <tr key={requisicao._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{requisicao.numero}</div>
                              {requisicao.urgente && (
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                  Urgente
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{requisicao.nomeInsumo}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {requisicao.quantidade} {requisicao.unidadeMedida}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{requisicao.responsavelRequisicao}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                <div className="font-medium">{obraInfo.nomeObra}</div>
                                <div className="text-xs text-gray-500">
                                  Etapa: {obraInfo.etapa}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(requisicao.status)}`}>
                                {getStatusLabel(requisicao.status)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {formatDate(requisicao.dataRequisicao)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => setEditingRequisicao(requisicao)}
                                  className="text-yellow-600 hover:text-yellow-900 p-1 rounded hover:bg-yellow-50"
                                  title="Editar"
                                >
                                  <Edit size={16} />
                                </button>
                                <button
                                  onClick={() => handleDelete(requisicao._id, 'requisicao')}
                                  className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                                  title="Excluir"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {/* Aprovação Tab */}
          {activeTab === 'aprovacao' && (
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Requisições Pendentes de Aprovação</h3>
              
              {requisicoesParaAprovacao.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma requisição pendente</h3>
                  <p className="mt-1 text-sm text-gray-500">Todas as requisições foram processadas</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {requisicoesParaAprovacao.map(requisicao => {
                    const obraInfo = getObraInfo(requisicao)
                    
                    return (
                      <div key={requisicao._id} className="bg-white border border-gray-200 rounded-lg p-6">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-4 mb-2">
                              <h4 className="text-lg font-semibold">{requisicao.numero}</h4>
                              {requisicao.urgente && (
                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                  <AlertTriangle size={12} className="mr-1" />
                                  Urgente
                                </span>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                              <div>
                                <span className="text-sm text-gray-600">Insumo:</span>
                                <p className="font-medium">{requisicao.nomeInsumo}</p>
                              </div>
                              <div>
                                <span className="text-sm text-gray-600">Quantidade:</span>
                                <p className="font-medium">{requisicao.quantidade} {requisicao.unidadeMedida}</p>
                              </div>
                              <div>
                                <span className="text-sm text-gray-600">Obra:</span>
                                <p className="font-medium">{obraInfo.nomeObra}</p>
                              </div>
                              <div>
                                <span className="text-sm text-gray-600">Responsável:</span>
                                <p className="font-medium">{requisicao.responsavelRequisicao}</p>
                              </div>
                            </div>
                            
                            {requisicao.observacoes && (
                              <div className="mb-4">
                                <span className="text-sm text-gray-600">Observações:</span>
                                <p className="text-sm bg-gray-50 p-2 rounded">{requisicao.observacoes}</p>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex gap-2 ml-4">
                            <button
                              onClick={() => handleApprove(requisicao._id)}
                              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center gap-2"
                            >
                              <CheckCircle size={16} />
                              Aprovar
                            </button>
                            <button
                              onClick={() => handleReject(requisicao._id)}
                              className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 flex items-center gap-2"
                            >
                              <XCircle size={16} />
                              Rejeitar
                            </button>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}

          {/* Cotação Tab */}
          {activeTab === 'cotacao' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Requisições Aprovadas para Cotação</h3>
                {selectedRequisicoes.length > 0 && (
                  <button
                    onClick={handleBatchQuotation}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                  >
                    Cotar Selecionadas ({selectedRequisicoes.length})
                  </button>
                )}
              </div>
              
              {requisicoesParaCotacao.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma requisição para cotação</h3>
                  <p className="mt-1 text-sm text-gray-500">Aguardando aprovação de requisições</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {requisicoesParaCotacao.map(requisicao => {
                    const obraInfo = getObraInfo(requisicao)
                    const isSelected = selectedRequisicoes.includes(requisicao._id)
                    
                    return (
                      <div key={requisicao._id} className={`bg-white border rounded-lg p-4 ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => handleSelectRequisicao(requisicao._id, e.target.checked)}
                              className="mr-3"
                            />
                            <div>
                              <h4 className="font-semibold">{requisicao.numero}</h4>
                              <p className="text-sm text-gray-600">
                                {requisicao.nomeInsumo} - {requisicao.quantidade} {requisicao.unidadeMedida}
                              </p>
                              <p className="text-xs text-gray-500">
                                {obraInfo.nomeObra} - {obraInfo.etapa}
                              </p>
                            </div>
                          </div>
                          
                          <div className="text-sm text-gray-500">
                            Aprovada em {formatDate(requisicao.dataAprovacao || requisicao.dataRequisicao)}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Requisições Cotadas */}
              {requisicoesCotadas.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-semibold mb-4">Requisições com Cotações</h3>
                  <div className="space-y-4">
                    {requisicoesCotadas.map(requisicao => {
                      const obraInfo = getObraInfo(requisicao)
                      
                      return (
                        <div key={requisicao._id} className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <h4 className="font-semibold">{requisicao.numero}</h4>
                              <p className="text-sm text-gray-600">
                                {requisicao.nomeInsumo} - {requisicao.quantidade} {requisicao.unidadeMedida}
                              </p>
                            </div>
                            <button
                              onClick={() => setViewingCotacoes(requisicao)}
                              className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                            >
                              Ver Cotações
                            </button>
                          </div>
                          
                          {requisicao.cotacoes.some((c: any) => c.selecionado) && (
                            <div className="mt-4">
                              <button
                                onClick={() => handleGenerateOrder(requisicao._id)}
                                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                              >
                                Gerar Ordem de Compra
                              </button>
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Ordens Tab */}
          {activeTab === 'ordens' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Ordens de Compra</h3>
                {ordensCompra.length > 0 && (
                  <div className="flex gap-2">
                    <button
                      onClick={handleExportOrdensPDF}
                      className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 flex items-center gap-2"
                    >
                      <FileText size={16} />
                      Exportar PDF
                    </button>
                    <button
                      onClick={handleExportOrdensExcel}
                      className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center gap-2"
                    >
                      <Download size={16} />
                      Exportar Excel
                    </button>
                  </div>
                )}
              </div>
              
              {ordensCompra.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma ordem de compra</h3>
                  <p className="mt-1 text-sm text-gray-500">Ordens aparecerão aqui após serem geradas</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Número OC
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fornecedor
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Insumo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Valor Total
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Data Emissão
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {ordensCompra.map(ordem => (
                        <tr key={ordem._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{ordem.numero}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{ordem.nomeFornecedor}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {ordem.nomeInsumo}
                              <div className="text-xs text-gray-500">
                                {ordem.quantidade} unidades
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {formatCurrency(ordem.valorTotal)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(ordem.status)}`}>
                              {getStatusLabel(ordem.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {formatDate(ordem.dataEmissao)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              {!ordem.enviadaFinanceiro && (
                                <button
                                  onClick={() => handleSendToFinance(ordem._id)}
                                  className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700 flex items-center gap-1"
                                  title="Enviar para Financeiro"
                                >
                                  <Send size={12} />
                                  Financeiro
                                </button>
                              )}
                              {ordem.enviadaFinanceiro && (
                                <span className="text-green-600 text-xs flex items-center gap-1">
                                  <CheckCircle size={12} />
                                  Enviada
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Fornecedores Tab */}
          {activeTab === 'fornecedores' && (
            <>
              <div className="p-6 border-b">
                <div className="flex justify-between items-center">
                  <div className="flex-1 relative mr-4">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      placeholder="Buscar fornecedores..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    onClick={() => setShowFornecedorForm(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Plus size={16} />
                    Novo Fornecedor
                  </button>
                </div>
              </div>

              <div className="p-6">
                {filteredFornecedores.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum fornecedor encontrado</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {searchTerm 
                        ? 'Tente ajustar os filtros de busca'
                        : 'Comece cadastrando seus fornecedores'
                      }
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredFornecedores.map(fornecedor => (
                      <div key={fornecedor._id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900">{fornecedor.nome}</h4>
                            <p className="text-sm text-gray-600">{fornecedor.segmento}</p>
                            <p className="text-sm text-gray-500">{fornecedor.contato}</p>
                          </div>
                          <div className="flex items-center gap-1">
                            {fornecedor.bloqueado && (
                              <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs flex items-center gap-1">
                                <AlertTriangle size={12} />
                                Bloqueado
                              </span>
                            )}
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              fornecedor.ativo ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {fornecedor.ativo ? 'Ativo' : 'Inativo'}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex space-x-2">
                          <button
                            onClick={() => setEditingFornecedor(fornecedor)}
                            className="flex-1 bg-yellow-600 text-white px-3 py-2 rounded-md hover:bg-yellow-700 text-sm"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleDelete(fornecedor._id, 'fornecedor')}
                            className="bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 text-sm"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal de Cotações */}
      {viewingCotacoes && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 z-10">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-900">
                  Cotações - {viewingCotacoes.numero}
                </h2>
                <button
                  onClick={() => setViewingCotacoes(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">
                  {viewingCotacoes.nomeInsumo}
                </h3>
                <p className="text-gray-600">
                  Quantidade: {viewingCotacoes.quantidade} {viewingCotacoes.unidadeMedida}
                </p>
              </div>

              <div className="space-y-4">
                {viewingCotacoes.cotacoes.map((cotacao: any, index: number) => (
                  <div
                    key={index}
                    className={`border rounded-lg p-4 ${
                      cotacao.selecionado ? 'border-green-500 bg-green-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-semibold">{cotacao.nomeFornecedor}</h4>
                        <p className="text-sm text-gray-600">
                          {formatCurrency(cotacao.precoUnitario)} por {viewingCotacoes.unidadeMedida}
                        </p>
                      </div>
                      {cotacao.selecionado ? (
                        <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-sm">
                          Selecionada
                        </span>
                      ) : (
                        <button
                          onClick={() => handleSelectQuotation(viewingCotacoes._id, index)}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                        >
                          Selecionar
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-3">
                      <div>
                        <span className="text-sm text-gray-600">Total:</span>
                        <p className="font-semibold">
                          {formatCurrency(cotacao.precoUnitario * viewingCotacoes.quantidade)}
                        </p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Prazo:</span>
                        <p className="font-semibold">{cotacao.prazoEntrega} dias</p>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Pagamento:</span>
                        <p className="font-semibold">{cotacao.condicoesPagamento}</p>
                      </div>
                    </div>

                    {cotacao.observacoes && (
                      <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
                        {cotacao.observacoes}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <RequisicaoForm
        isOpen={showRequisicaoForm}
        onClose={() => setShowRequisicaoForm(false)}
        onSubmit={createRequisicao}
        title="Criar Nova Requisição"
      />

      <RequisicaoForm
        isOpen={!!editingRequisicao}
        onClose={() => setEditingRequisicao(null)}
        onSubmit={(data) => updateRequisicao(editingRequisicao._id, data)}
        initialData={editingRequisicao}
        title="Editar Requisição"
      />

      <CotacaoLoteForm
        isOpen={showCotacaoLoteForm}
        onClose={() => {
          setShowCotacaoLoteForm(false)
          setSelectedRequisicoes([])
        }}
        onSubmit={addCotacoes}
        requisicoesSelecionadas={requisicoesParaCotacao.filter(r => selectedRequisicoes.includes(r._id))}
      />

      <FornecedorForm
        isOpen={showFornecedorForm}
        onClose={() => setShowFornecedorForm(false)}
        onSubmit={createFornecedor}
        title="Criar Novo Fornecedor"
      />

      <FornecedorForm
        isOpen={!!editingFornecedor}
        onClose={() => setEditingFornecedor(null)}
        onSubmit={(data) => updateFornecedor(editingFornecedor._id, data)}
        initialData={editingFornecedor}
        title="Editar Fornecedor"
      />
    </div>
  )
}

export default Compras
